import { feedbacks } from '../../fakedata/feedbacks';
import { analytics } from '@/fakedata/analytics';
import { departments } from '@/fakedata/departments';
import { teams } from '@/fakedata/teams';
import { users } from '@/fakedata/users';
import { allDepartmentsForDropdown } from '@/fakedata/allDepartmentsForDropdown';
import { allTeamsForDropdown } from '@/fakedata/allTeamsForDropdown';
import { companyInsights } from '@/fakedata/company-insights';
import { mockedSlackWorkspaces, mockedTeamsTenants } from '@/fakedata/integrations';
export { fakeBackend };

// TODO: move to fakedata folder
interface User {
  id: string;
  role: string;
  email: string;
  fullName: string;
  sex: string;
  relationshipStatus: string;
  kids: number;
  techAffinity: number;
  birthDate: string;
  identifiesAs: string;
  joinedAt: string;
  imageUrl: string;
  runtimeContext: {
    type: string;
    companyId: string | null;
    psychoCompanyId: string | null;
    activeFrom: string | null;
  };
}

// TODO: move to fakedata folder
const defaultUser: User = {
  id: '123e4567-e89b-12d3-a456-426614174000',
  role: 'company-manager',
  email: 'test@apple.com',
  fullName: 'Тест Менеджер',
  sex: 'man',
  relationshipStatus: 'single',
  kids: 2,
  techAffinity: 8,
  birthDate: '1974-01-01T00:00:00Z',
  identifiesAs: 'man',
  joinedAt: '2023-01-01T00:00:00Z',
  imageUrl:
    'https://images.unsplash.com/photo-1701513519108-b0a234f26161?q=80&w=2466&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  runtimeContext: {
    type: 'b2b',
    companyId: 'demo-company-id',
    psychoCompanyId: null,
    activeFrom: '2023-01-01T00:00:00Z',
  },
};
let mockedSlackWorkspaceState = mockedSlackWorkspaces.map((workspace) => ({ ...workspace }));
let mockedTeamsTenantState = mockedTeamsTenants.map((tenant) => ({ ...tenant }));
let mockedTeamsPendingLinkState: Record<
  string,
  {
    tenantId: string;
    tenantDisplayName: string | null;
    teamsUserDisplayName: string | null;
    expiresAt: string;
    linkedCompanyId: string | null;
  }
> = {
  'valid-teams-token': {
    tenantId: 'tenant-demo-5',
    tenantDisplayName: 'Scarb Support Tenant',
    teamsUserDisplayName: 'Jane Manager',
    expiresAt: '2026-05-28T13:00:00.000Z',
    linkedCompanyId: null,
  },
  'valid-teams-token-no-name': {
    tenantId: 'tenant-demo-6',
    tenantDisplayName: null,
    teamsUserDisplayName: 'Olena Manager',
    expiresAt: '2026-05-28T13:15:00.000Z',
    linkedCompanyId: null,
  },
  'linked-other-company-token': {
    tenantId: 'tenant-demo-7',
    tenantDisplayName: 'Already Linked Tenant',
    teamsUserDisplayName: 'Alex Admin',
    expiresAt: '2026-05-28T13:30:00.000Z',
    linkedCompanyId: 'other-company-id',
  },
};
const isDemo = import.meta.env.VITE_IS_DEMO === 'true';

function fakeBackend() {
  const realFetch = window.fetch;
  window.fetch = function (url: string, opts: { method: string; headers: { [key: string]: string }; body?: string }) {
    return new Promise<Response>((resolve, reject) => {
      setTimeout(handleRoute, 500);

      function handleRoute() {
        if (!isDemo) return realFetch(url, opts).then(resolve).catch(reject);

        const urlObj = new URL(url, window.location.origin);
        const baseApiPath = new URL(import.meta.env.VITE_API_URL, window.location.origin).pathname;
        const path = urlObj.pathname.replace(baseApiPath, '').replace(/^\/v\d+/, ''); // Remove version prefix

        // --- Authentication ---
        if (path.endsWith('/auth/login')) return authenticate();
        if (path.endsWith('/auth/company-registration')) return ok({});
        if (path.endsWith('/auth/forgot-password')) return ok({});
        if (path.endsWith('/auth/create-invite')) return getInvite();
        if (path.endsWith('/auth/create-psycho-company-invite')) return getInvite();
        if (path === '/profile') return getProfile();

        // --- Analytics ---
        if (path === '/analytics/mood') return getAnalytics(analytics.mood);
        if (path === '/analytics/symptoms') return getAnalytics(analytics.symptoms);
        if (path === '/analytics/metrics') return getAnalytics(analytics.metrics);
        if (path === '/analytics/gauge') return getAnalytics(analytics.gauge);
        if (path === '/analytics/mental-states') return getAnalytics(analytics.mentalStates);
        if (path === '/analytics/progress') return getAnalytics(analytics.progress);

        // --- Core data ---
        if (path === '/departments') return getDepartments(urlObj);
        if (path === '/departments/all') return getAllDepartments();
        if (path === '/teams') return getTeams(urlObj);
        if (path === '/teams/all') return getAllTeams();
        if (path === '/users') return getUsers(urlObj);
        if (path === '/company-insights') return getCompanyInsights(urlObj);
        if (path === '/feedback') return getFeedbacks(urlObj);
        if (path === '/company-insights/order') return ok({});
        if (path === '/integrations/slack/install-url') return getSlackInstallUrl();
        if (/^\/integrations\/slack\/status\/[^/]+$/.test(path)) return getSlackStatus(urlObj);
        if (/^\/integrations\/slack\/workspaces\/[^/]+$/.test(path) && opts.method === 'PATCH') return updateSlackWorkspace(path);
        if (path === '/integrations/teams/link' && opts.method === 'GET') return getTeamsLinkPreview(urlObj);
        if (path === '/integrations/teams/link/confirm' && opts.method === 'POST') return confirmTeamsLink();
        if (/^\/integrations\/teams\/status\/[^/]+$/.test(path)) return getTeamsStatus(urlObj);
        if (/^\/integrations\/teams\/tenants\/[^/]+$/.test(path) && opts.method === 'PATCH') return updateTeamsTenant(path);
        if (/^\/feedback\/[^/]+$/.test(path)) return getFeedbackById(path);
        if (/^\/departments\/[^/]+$/.test(path)) return ok({});
        if (/^\/teams\/[^/]+$/.test(path)) return ok({});
        if (/^\/users\/[^/]+$/.test(path)) return ok({});
        if (/^\/psychologist\/company-membership\/[^/]+\/terminate$/.test(path)) return ok({ success: true });
        if (/^\/company-insights\/[^/]+$/.test(path)) return getCompanyInsightById(path);

        // Fallback to real API
        return realFetch(url, opts).then(resolve).catch(reject);
      }

      function authenticate() {
        const { email } = body();
        return email.endsWith('@apple.com') || email.endsWith('@gmail.com')
          ? ok({ accessToken: 'fake-jwt-token', refreshToken: 'fake-refresh-token' })
          : error('Invalid credentials');
      }

      function getProfile() {
        return ok(defaultUser);
      }

      function getAnalytics(data: unknown) {
        return ok(data);
      }

      function getAllDepartments() {
        return ok(allDepartmentsForDropdown);
      }

      function getAllTeams() {
        return ok(allTeamsForDropdown);
      }

      function getInvite() {
        const inviteLink = 'https://example.com/invite/abc123';
        const response = {
          success: true,
          code: 'invite_created',
          data: {
            link: inviteLink,
          },
        };
        return ok(response);
      }

      function getSlackInstallUrl() {
        return ok({
          installUrl: 'https://slack.com/oauth/v2/authorize?client_id=fake-client-id&scope=commands',
        });
      }

      function getSlackStatus(urlObj: URL) {
        const page = Number(urlObj.searchParams.get('page')) || 1;
        const limit = Number(urlObj.searchParams.get('limit')) || 10;
        const normalizedSearch = urlObj.searchParams.get('name')?.trim().toLowerCase();
        const filteredItems = normalizedSearch
          ? mockedSlackWorkspaceState.filter((workspace) => workspace.teamName.toLowerCase().includes(normalizedSearch))
          : mockedSlackWorkspaceState;
        const startIndex = (page - 1) * limit;

        return ok({
          items: filteredItems.slice(startIndex, startIndex + limit),
          meta: {
            page,
            limit,
            total: filteredItems.length,
            totalPages: Math.ceil(filteredItems.length / limit),
          },
        });
      }

      function updateSlackWorkspace(path: string) {
        const teamId = path.split('/').pop();
        const payload = body();
        const existingWorkspace = mockedSlackWorkspaceState.find((workspace) => workspace.teamId === teamId);

        if (!existingWorkspace) {
          return error('Workspace not found');
        }

        const updatedWorkspace = {
          ...existingWorkspace,
          ...(payload.teamName !== undefined && { teamName: payload.teamName }),
          ...(payload.status !== undefined && { status: payload.status }),
        };

        mockedSlackWorkspaceState = mockedSlackWorkspaceState.map((workspace) =>
          workspace.teamId === teamId ? updatedWorkspace : workspace,
        );

        return ok(updatedWorkspace);
      }

      function getTeamsStatus(urlObj: URL) {
        const page = Number(urlObj.searchParams.get('page')) || 1;
        const limit = Number(urlObj.searchParams.get('limit')) || 10;
        const linkedTenants = mockedTeamsTenantState
          .filter((tenant) => tenant.status === 'active' && tenant.linkedAt)
          .sort((leftTenant, rightTenant) => {
            return (rightTenant.linkedAt || '').localeCompare(leftTenant.linkedAt || '');
          });
        const startIndex = (page - 1) * limit;

        return ok({
          items: linkedTenants.slice(startIndex, startIndex + limit),
          meta: {
            page,
            limit,
            total: linkedTenants.length,
            totalPages: Math.ceil(linkedTenants.length / limit),
          },
        });
      }

      function getTeamsLinkPreview(urlObj: URL) {
        const token = urlObj.searchParams.get('t') || '';
        const preview = mockedTeamsPendingLinkState[token];

        if (!preview) {
          return errorResponse('Invalid Teams link token', 'teams_pending_link_invalid');
        }

        return ok(preview);
      }

      function confirmTeamsLink() {
        const payload = body();
        const preview = mockedTeamsPendingLinkState[payload.token];

        if (!preview) {
          return errorResponse('Invalid Teams link token', 'teams_pending_link_invalid');
        }

        if (preview.linkedCompanyId && preview.linkedCompanyId !== payload.companyId) {
          return errorResponse(
            'Tenant already linked to another company',
            'teams_tenant_already_linked',
            409,
          );
        }

        const existingTenant = mockedTeamsTenantState.find((tenant) => tenant.tenantId === preview.tenantId);
        const linkedAt = new Date().toISOString();
        const tenant = {
          id: existingTenant?.id ?? `teams-tenant-${mockedTeamsTenantState.length + 1}`,
          companyId: payload.companyId,
          tenantId: preview.tenantId,
          tenantDisplayName: preview.tenantDisplayName,
          status: 'active' as const,
          linkedByUserId: defaultUser.id,
          linkedAt,
          createdAt: existingTenant?.linkedAt ?? linkedAt,
          updatedAt: linkedAt,
        };

        if (existingTenant) {
          mockedTeamsTenantState = mockedTeamsTenantState.map((currentTenant) =>
            currentTenant.id === existingTenant.id ? { ...currentTenant, ...tenant } : currentTenant,
          );
        } else {
          mockedTeamsTenantState = [tenant, ...mockedTeamsTenantState];
        }

        delete mockedTeamsPendingLinkState[payload.token];

        return ok(tenant);
      }

      function updateTeamsTenant(path: string) {
        const tenantId = path.split('/').pop();
        const payload = body();
        const existingTenant = mockedTeamsTenantState.find((tenant) => tenant.id === tenantId);

        if (!existingTenant) {
          return errorResponse('Teams tenant not found', 'teams_tenant_not_found', 404);
        }

        const updatedTenant = {
          ...existingTenant,
          ...(payload.displayName !== undefined && { tenantDisplayName: payload.displayName }),
          ...(payload.status !== undefined && { status: payload.status }),
          updatedAt: new Date().toISOString(),
        };

        mockedTeamsTenantState = mockedTeamsTenantState.map((tenant) =>
          tenant.id === tenantId ? updatedTenant : tenant,
        );

        return ok(updatedTenant);
      }

      function getCompanyInsights(urlObj: URL) {
        const status = urlObj.searchParams.get('status');
        let filtered = companyInsights.items;

        if (status) {
          filtered = filtered.filter((insight: any) => insight.status === status);
        }

        return ok({
          items: filtered,
          meta: {
            page: 1,
            limit: filtered.length,
            total: filtered.length,
            totalPages: 1,
          },
        });
      }

      function getCompanyInsightById(path: string) {
        const id = path.split('/').pop();
        const insight = companyInsights.items.find((f: any) => f.id === id);
        return insight ? ok(insight) : error('Insight not found');
      }

      function getDepartments(urlObj: URL) {
        const filtered = filterAndSort(departments.items, urlObj, {
          allowedSortFields: ['name', 'teamsCount'],
          searchFn: (dep, search) => dep.name.toLowerCase().includes(search),
        });

        return ok(paginate(filtered, urlObj));
      }

      function getTeams(urlObj: URL) {
        const departmentId = urlObj.searchParams.get('departmentId');

        const filtered = filterAndSort(teams.items, urlObj, {
          allowedSortFields: ['createdAt', 'name', 'employeeCount'],
          searchFn: (team, search) => team.name.toLowerCase().includes(search) || team.description?.toLowerCase().includes(search),
          extraFilterFn: departmentId ? (team) => team.departments.some((dep) => dep.id === departmentId) : undefined,
        });

        return ok(paginate(filtered, urlObj));
      }
      function getUsers(urlObj: URL) {
        const teamId = urlObj.searchParams.get('teamId');

        const filtered = filterAndSort(users.items, urlObj, {
          allowedSortFields: ['createdAt', 'email', 'role', 'status', 'fullName'],
          searchFn: (user, search) => user.fullName.toLowerCase().includes(search) || user.email.toLowerCase().includes(search),
          extraFilterFn: teamId ? (user) => user.teams.some((team) => team.id === teamId) : undefined,
        });

        return ok(paginate(filtered, urlObj));
      }

      function getFeedbacks(urlObj: URL) {
        const filtered = filterAndSort(feedbacks.items, urlObj, {
          allowedSortFields: ['createdAt', 'state'],
          searchFn: (feedback, search) =>
            (feedback.title ?? '').toLowerCase().includes(search) ||
            (feedback.content ?? '').toLowerCase().includes(search) ||
            (feedback.creatorName ?? '').toLowerCase().includes(search),
        });

        return ok(paginate(filtered, urlObj));
      }

      function getFeedbackById(path: string) {
        const id = path.split('/').pop();
        const feedback = feedbacks.items.find((f: any) => f.id === id);

        return feedback ? ok(feedback) : error('Feedback not found');
      }

      function ok(responseBody: unknown): void {
        resolve({
          ok: true,
          status: 200,
          text: () => Promise.resolve(JSON.stringify(responseBody)),
          json: () => Promise.resolve(responseBody),
        } as Response);
      }

      function error(message: string): void {
        errorResponse(message);
      }

      function errorResponse(message: string, code = 'generic_error', status = 400): void {
        resolve({
          ok: false,
          status,
          text: () => Promise.resolve(JSON.stringify({ message, code })),
          json: () => Promise.resolve({ message, code }),
        } as Response);
      }

      function body() {
        return opts.body && JSON.parse(opts.body);
      }

      function filterAndSort<T>(
        items: T[],
        urlObj: URL,
        options: {
          allowedSortFields: string[];
          searchFn?: (item: T, search: string) => boolean;
          extraFilterFn?: (item: T) => boolean;
        },
      ): T[] {
        const search = urlObj.searchParams.get('search')?.toLowerCase() || '';
        const sortBy = urlObj.searchParams.get('sortBy') || '';
        const sortOrder = (urlObj.searchParams.get('sortOrder') || 'ASC').toUpperCase();

        let filtered = [...items];

        if (search && options.searchFn) {
          filtered = filtered.filter((item) => options.searchFn!(item, search));
        }

        if (options.extraFilterFn) {
          filtered = filtered.filter(options.extraFilterFn);
        }

        if (options.allowedSortFields.includes(sortBy)) {
          filtered = filtered.sort((a: any, b: any) => {
            const valA = a[sortBy];
            const valB = b[sortBy];

            if (typeof valA === 'string') {
              return sortOrder === 'DESC' ? valB.localeCompare(valA) : valA.localeCompare(valB);
            }

            if (typeof valA === 'number') {
              return sortOrder === 'DESC' ? valB - valA : valA - valB;
            }

            return 0;
          });
        }

        return filtered;
      }

      function paginate(items: any[], urlObj: URL) {
        const page = parseInt(urlObj.searchParams.get('page') || '1');
        const limit = parseInt(urlObj.searchParams.get('limit') || '10');
        const start = (page - 1) * limit;
        const end = start + limit;
        const pagedItems = items.slice(start, end);
        return {
          items: pagedItems,
          meta: {
            page,
            limit,
            total: items.length,
            totalPages: Math.ceil(items.length / limit),
          },
        };
      }
    });
  } as typeof window.fetch;
}
